import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Camera, X, RefreshCw, CheckCircle } from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// CameraCapture — Modal pour capturer une photo via webcam (desktop) ou caméra
// arrière (mobile via getUserMedia).
// ─────────────────────────────────────────────────────────────────────────────
// Usage :
//   const [cameraOuverte, setCameraOuverte] = useState(false);
//   ...
//   {cameraOuverte && (
//     <CameraCapture
//       onCapture={(files) => uploadPhotos(files)}
//       onClose={() => setCameraOuverte(false)}
//     />
//   )}
//
// `onCapture` reçoit un tableau de 1 File JPEG (cohérent avec les upload
// multi-fichiers du reste de l'app).
//
// Sur mobile, `facingMode: 'environment'` cible la caméra arrière (mieux pour
// photographier un produit). Bouton flip pour passer en frontale.
// ─────────────────────────────────────────────────────────────────────────────

export default function CameraCapture({ onCapture, onClose, titre = 'Prendre une photo' }) {
  const videoRef  = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [ready,    setReady]    = useState(false);
  const [captured, setCaptured] = useState(null);    // dataURL de l'aperçu
  const [facingBack, setFacingBack] = useState(true);
  const [erreur,   setErreur]   = useState(null);

  const demarrerCamera = useCallback(async (back = true) => {
    // Arrêter le flux précédent avant d'en démarrer un nouveau
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
    }
    setCaptured(null);
    setReady(false);
    setErreur(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: back ? 'environment' : 'user',
          width:  { ideal: 1280 },
          height: { ideal: 960 },
        },
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => setReady(true);
      }
    } catch (err) {
      setErreur(
        err.name === 'NotAllowedError'
          ? 'Accès à la caméra refusé. Autorise-le dans les réglages du navigateur.'
          : 'Impossible d\'accéder à la caméra (aucun périphérique détecté ?).'
      );
    }
  }, []);

  useEffect(() => {
    demarrerCamera(true);
    // Cleanup au démontage : on libère bien le flux pour éteindre la LED caméra
    return () => {
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    };
  }, [demarrerCamera]);

  const capturer = () => {
    const video  = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    canvas.width  = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    setCaptured(canvas.toDataURL('image/jpeg', 0.92));
  };

  const reprendre = () => {
    setCaptured(null);
    demarrerCamera(facingBack);
  };

  const confirmer = () => {
    if (!captured) return;
    // Convertir le dataURL en File pour pouvoir l'envoyer en multipart
    const [meta, b64] = captured.split(',');
    const mime = meta.match(/:(.*?);/)[1];
    const bin  = atob(b64);
    const u8   = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
    const file = new File(
      [new Blob([u8], { type: mime })],
      `capture_${Date.now()}.jpg`,
      { type: mime }
    );
    onCapture([file]);
    onClose();
  };

  const basculerCamera = () => {
    const suivant = !facingBack;
    setFacingBack(suivant);
    demarrerCamera(suivant);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-lg overflow-hidden">
        {/* En-tête */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
          <h3 className="text-white font-semibold flex items-center gap-2">
            <Camera size={16} className="text-yellow-400" /> {titre}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white p-1">
            <X size={18} />
          </button>
        </div>

        {/* Aperçu vidéo / capture */}
        <div className="relative bg-black" style={{ aspectRatio: '4/3' }}>
          {erreur ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-center px-6">
              <Camera size={36} className="text-gray-600" />
              <p className="text-red-400 text-sm">{erreur}</p>
              <button
                onClick={() => demarrerCamera(facingBack)}
                className="px-4 py-2 bg-yellow-600 hover:bg-yellow-500 text-black font-medium rounded-lg text-sm"
              >
                Réessayer
              </button>
            </div>
          ) : captured ? (
            <img src={captured} alt="Aperçu" className="w-full h-full object-contain" />
          ) : (
            <>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover"
              />
              {!ready && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-8 h-8 border-2 border-yellow-500 border-t-transparent rounded-full animate-spin" />
                </div>
              )}
              {/* Grille viseur (règle des tiers) */}
              {ready && (
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    backgroundImage:
                      'linear-gradient(rgba(255,255,255,.08) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.08) 1px,transparent 1px)',
                    backgroundSize: '33.33% 33.33%',
                  }}
                />
              )}
            </>
          )}
          <canvas ref={canvasRef} className="hidden" />

          {/* Bouton flip caméra (mobile principalement) */}
          {!captured && !erreur && (
            <button
              onClick={basculerCamera}
              className="absolute top-3 right-3 p-2 bg-black/50 text-white rounded-full hover:bg-black/70 transition-colors"
              title="Changer de caméra"
            >
              <RefreshCw size={16} />
            </button>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-center gap-4 px-4 py-4">
          {captured ? (
            <>
              <button
                onClick={reprendre}
                className="flex-1 py-2.5 bg-gray-800 hover:bg-gray-700 text-gray-200 rounded-xl text-sm font-medium flex items-center justify-center gap-2"
              >
                <RefreshCw size={14} /> Reprendre
              </button>
              <button
                onClick={confirmer}
                className="flex-1 py-2.5 bg-yellow-600 hover:bg-yellow-500 text-black rounded-xl text-sm font-bold flex items-center justify-center gap-2"
              >
                <CheckCircle size={14} /> Utiliser cette photo
              </button>
            </>
          ) : (
            <>
              <button
                onClick={onClose}
                className="px-5 py-2.5 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-xl text-sm font-medium"
              >
                Annuler
              </button>
              <button
                onClick={capturer}
                disabled={!ready}
                className="w-16 h-16 rounded-full bg-white disabled:bg-gray-600 hover:bg-gray-200 border-4 border-yellow-500 flex items-center justify-center transition-all disabled:border-gray-500"
                title="Capturer"
              >
                <div className="w-12 h-12 rounded-full bg-white" />
              </button>
              {/* Spacer pour centrer le bouton capture */}
              <div className="px-5 py-2.5 opacity-0 select-none text-sm">Annuler</div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
